import React, { Component } from "react";
import ReactJson from "react-json-view";
import {
    AnyProcessorItemError,
    ProcessorItem,
    shortenProcessorItemErrorType
} from "../../models/MailroomProcessorItem";
import Table, { TableColumn } from "../Table";
import * as _ from "lodash";
import TableCell from "../TableCell";
import { CommandBarButton, Icon } from "office-ui-fabric-react";
import ProcessorItemTable from "./ProcessorItemTable";
import { ProcessorItemRow, ProcessorItemRows } from "./ProcessorItemRows";

interface ProcessorItemDetailCardProps {
    item: ProcessorItem;
    relatedItems: ProcessorItem[];
    allItems: ProcessorItem[];
    onRelatedItemSelected(item: ProcessorItem): void;
}

interface ProcessorItemDetailCardState {
    showJson: boolean;
    expandedTableCells: { [key: string]: boolean; }
}

export default class ProcessorItemDetailCard extends Component<ProcessorItemDetailCardProps, ProcessorItemDetailCardState>
{
    static misColumns: TableColumn[] = [
        {
            title: "MIS Fields",
            propertyPath: "field",
            flex: 1,
        },
    ];

    static sdsColumns: TableColumn[] = [
        {
            title: "SDS Fields",
            propertyPath: "field",
            flex: 1,
        },
    ];

    static errorColumns: TableColumn[] = [
        {
            title: "Type",
            propertyPath: "$type",
            flex: 1,
        },
        {
            title: "Detail",
            extraDetail: "Step and/or field where error occurred",
            propertyPath: "step",
            flex: 2,
        },
        {
            title: "Message",
            propertyPath: "message",
            flex: 3,
        }
    ]

    constructor(props: ProcessorItemDetailCardProps) {
        super(props);

        this.state = {
            showJson: false,
            expandedTableCells: {}
        }
    }

    private fieldCellForColumn = (item: ProcessorItemRow, column: TableColumn, index: number) => {
        if (Array.isArray(item.value)) {
            return (
                <TableCell flex={column.flex} className="clickable" onClick={() => this.onCellClicked(item)}>
                    <span>
                        <Icon iconName={this.state.expandedTableCells[this.expandedTableCellsKey(item)] === true ? "ChevronUpSmall" : "ChevronDownSmall"} className="m-right-05rem" />
                        <span className={"cdb-table__extra-detail"} style={{ verticalAlign: "top", paddingTop: 0 }}> {item.key} </span>
                    </span>
                    {this.state.expandedTableCells[this.expandedTableCellsKey(item)] === true ? this.fieldCellObjectsForColumn(item.value) : null}
                </TableCell>
            );
        }
        else {
            return (
                <TableCell flex={column.flex}>
                    <span className={"cdb-table__extra-detail"}>{item.key}</span>
                    <span>{item.value}</span>
                </TableCell>
            );
        }
    }

    private onCellClicked = (item: ProcessorItemRow) => {
        this.state.expandedTableCells[this.expandedTableCellsKey(item)] = !this.state.expandedTableCells[this.expandedTableCellsKey(item)];
        this.setState({ expandedTableCells: this.state.expandedTableCells });
    }

    private expandedTableCellsKey = (item: ProcessorItemRow) => `${item.type}-${item.key}`;

    private fieldCellObjectsForColumn = (items: ProcessorItemRow[]) => {
        if (items === null || items.length === 0) {
            return <div className="flex-column m-left-1rem">
                <span className={"cdb-table__extra-detail"}>No items</span>
            </div>
        }

        return items.map((item) => {
            if (Array.isArray(item.value)) {
                return <div className="flex-column m-left-1rem">
                    <span className={"cdb-table__extra-detail"}>{item.key}</span>
                    {this.fieldCellObjectsForColumn(item.value)}
                </div>
            }
            else {
                return <div className="flex-column m-left-1rem">
                    <span className={"cdb-table__extra-detail"}>{item.key}</span>
                    <span>{item.value}</span>
                </div>
            }
        });
    }

    private errorCellForColumn = (item: AnyProcessorItemError, column: TableColumn, index: number) => {

        const value = item[column.propertyPath];

        if (column.propertyPath === "$type") {
            return (
                <TableCell flex={column.flex}>
                    <span>{shortenProcessorItemErrorType(value)}</span>
                </TableCell>
            )
        }

        if (column.propertyPath === "step") {
            if (item.field) {
                return (
                    <TableCell flex={column.flex}>
                        <span>{item.field}</span>
                        <span className={"cdb-table__extra-detail"}>{value}</span>
                    </TableCell>
                )
            }
            else {
                return (
                    <TableCell flex={column.flex}>
                        <span>{value}</span>
                    </TableCell>
                )
            }
        }

        if (column.propertyPath === "message") {
            return (
                <TableCell flex={column.flex}>
                    <span>{value}</span>
                    <span className={"cdb-table__extra-detail"}>{item.help}</span>
                </TableCell>
            )
        }
    }

    public componentDidMount() {
    }

    private getErrorTable() {
        if (!this.props.item?.errors) {
            return;
        }

        return <Table className={"cdb-table--no-margin"} columns={ProcessorItemDetailCard.errorColumns} items={this.props.item.errors} title={`${this.props.item.errors.length} Errors`} cellForColumn={this.errorCellForColumn} />
    }

    private buildMainContent() {
        const misRows = ProcessorItemRows.MakeRowsFromItem(this.props.item.misItem, "mis", false);
        const sdsRows = ProcessorItemRows.MakeRowsFromItem(this.props.item.sdsRow, "sds", true);
        
        return (
            <div className={"flex-column"}>
                <div className={"flex-row"}>
                    <Table className={"flex-item--flex-1 cdb-table--no-margin"} hideHeaderTitle={true} items={misRows} columns={ProcessorItemDetailCard.misColumns} cellForColumn={this.fieldCellForColumn} />
                    <Table className={"flex-item--flex-1 cdb-table--no-margin"} hideHeaderTitle={true} items={sdsRows} columns={ProcessorItemDetailCard.sdsColumns} cellForColumn={this.fieldCellForColumn} />
                </div>

                <ProcessorItemTable className={"cdb-table--no-margin"} title={`${this.props.relatedItems.length} Related Processor Items`} allItems={this.props.allItems} shownItems={this.props.relatedItems} virtualize={false} onItemClicked={this.props.onRelatedItemSelected} />

                {this.getErrorTable()}
            </div>
        );
    }

    public render() {

        const jsonButtonIcon = this.state.showJson ? "Cancel" : "Code";
        const jsonButtonText = this.state.showJson ? "Close JSON" : "Show JSON";

        const jsonButton = (
            <CommandBarButton
                iconProps={{ iconName: jsonButtonIcon }}
                text={jsonButtonText}
                onClick={() => {
                    this.setState({ showJson: !this.state.showJson })
                }}
            />
        );

        const pillText = this.props.item.error ? `${this.props.item.errors.length} Error(s)` : "Success";
        const pillColour = this.props.item.error ? "red" : "green";

        return (
            <div className="card">
                <div className="card__header">
                    <span className={"card__title"}>
                        {shortenProcessorItemErrorType(this.props.item.$type)} Processor Item
                        <br />
                        <span className={"text-extra-detail"}>#{this.props.item.$id} </span><span className={`pill pill--tiny pill--${pillColour}`}>{pillText}</span>
                    </span>

                    {jsonButton}
                </div>

                {this.state.showJson ? <ReactJson src={this.props.item} theme="rjv-default" /> : this.buildMainContent()}

            </div>

        );
    }
}